import type { FailureCategoryR, JobState } from '../api/types';
import { FAILURE_CATEGORY_R_IS_REAL_FAILURE, FAILURE_CATEGORY_R_LABEL } from '../api/types';
import { translateJobState } from '../lib/state_labels';

/** Compact ``[dot] LABEL`` indicator for a job's state.
 *
 *  Colour comes from ``.status-dot.{state}`` / ``.status-label.{state}``
 *  in ``base.css``, which uses the design tokens so light/dark themes
 *  swap correctly.
 *
 *  Phase 4.9 — consolidated onto the central ``translateJobState``
 *  map so the badge label matches the timeline label for the same
 *  state. The previous local map used noun forms («ожидание»,
 *  «на сервисе»); the unified verb forms («ожидает», «отправлено
 *  на сервис») read consistently between the badge and the
 *  «timeline:» surface where the same state appears moments later.
 *
 *  «Не применимо» fix — a FAILED job whose Phase R category is NOT a
 *  real failure (``not_applicable`` / ``file_unavailable`` / ``other``)
 *  is a catalogue/infrastructure limit, not a system error. When the
 *  caller passes such a ``failureCategory`` the pill renders the neutral
 *  category label with a muted ``neutral`` style instead of the red
 *  «ОШИБКА» — mirroring the ``isRealFailure`` branch JobCard already
 *  uses for the row's right-meta, so the two agree. ``service_failed``
 *  (the real-failure bucket) and callers that omit ``failureCategory``
 *  (e.g. DetailScreen) keep the plain state pill. */
export function StatusPill({
  state,
  failureCategory,
}: {
  state: JobState;
  failureCategory?: FailureCategoryR | null;
}) {
  const isNeutralFailure =
    state === 'failed' &&
    failureCategory != null &&
    FAILURE_CATEGORY_R_IS_REAL_FAILURE[failureCategory] === false;

  if (isNeutralFailure) {
    return (
      <span className="status-pill">
        <span className="status-dot neutral" aria-hidden />
        <span className="status-label neutral" data-testid="status-pill-neutral">
          {FAILURE_CATEGORY_R_LABEL[failureCategory]}
        </span>
      </span>
    );
  }

  return (
    <span className="status-pill">
      <span className={`status-dot ${state}`} aria-hidden />
      <span className={`status-label ${state}`}>{translateJobState(state)}</span>
    </span>
  );
}
